// web/src/crypto/types.ts — local crypto / wire types for the client crypto module.
//
// Authoritative contract: shared/crypto-contract.md (ADR-001, D-014). These types
// live in /web only — they are the in-browser confidentiality surface and must NOT
// be added to shared/types.ts (the server never reasons about key material).
//
// Hard invariant (R11): the only network-bound artefacts are `WireShard`,
// `PublicKeyJwk` and the fingerprint — all opaque ciphertext or public material.
// `DataKey`, `RawShare`, and any `CryptoKey` private handle never cross a function
// boundary that leads to the network.
//
// Wire/format note: every wire structure carries `v: 1`. Versioning is additive
// and immutable — a format change adds a new union member / `v` value; we never
// reinterpret an existing version.

// ---------------------------------------------------------------------------
// Versioning & branding primitives
// ---------------------------------------------------------------------------

/** Wire-format version. Bump only by adding a new union member; never reinterpret v:1. */
export type CryptoFormatVersion = 1;
export const CRYPTO_FORMAT_VERSION: CryptoFormatVersion = 1;

/** base64url (RFC 4648 §5, no padding). All binary wire fields use this. */
export type Base64Url = string & { readonly __brand: 'Base64Url' };

/** Stable identifier for a participant's public key (= its fingerprint hash, hex). */
export type PublicKeyId = string & { readonly __brand: 'PublicKeyId' };

/** A P-256 public key exported as JWK (the only key form that leaves the device). */
export interface PublicKeyJwk {
  kty: 'EC';
  crv: 'P-256';
  x: string; // base64url
  y: string; // base64url
  ext: true;
  key_ops: [];
}

// ---------------------------------------------------------------------------
// Envelope codec (envelope scheme — only the data key is split)
// ---------------------------------------------------------------------------

/** Opaque, server-storable ciphertext of the secret. Contains NO key material. */
export interface Envelope {
  v: CryptoFormatVersion;
  alg: 'AES-256-GCM';
  iv: Base64Url; // 96-bit random IV
  ciphertext: Base64Url; // AES-GCM output (includes the auth tag)
}

/** The 256-bit data key, in-browser only. NEVER serialized to the wire. */
export type DataKey = Uint8Array & { readonly __brand: 'DataKey' };

export interface EnvelopeCodec {
  /** Generate a fresh 256-bit data key (crypto.getRandomValues). */
  generateDataKey(): DataKey;

  /** AES-256-GCM encrypt `plaintext` under `dataKey`. */
  seal(plaintext: Uint8Array, dataKey: DataKey): Promise<Envelope>;

  /** AES-256-GCM decrypt. Throws CryptoError('envelope_tampered') on tag failure. */
  open(envelope: Envelope, dataKey: DataKey): Promise<Uint8Array>;
}

// ---------------------------------------------------------------------------
// Shamir split / combine
// ---------------------------------------------------------------------------

/** A raw Shamir share of the data key, in-browser only (hex, secrets.js-grempe). */
export type RawShare = string & { readonly __brand: 'RawShare' };

export interface ShamirCodec {
  /**
   * Split a data key into `n` shares, threshold `k`. 2 <= k <= n <= 255.
   * Output order is not significant; each share self-describes its index.
   */
  split(dataKey: DataKey, n: number, k: number): RawShare[];

  /**
   * Combine shares back into the data key. Requires >= k *correct* shares.
   * Fewer than k, or k-1 valid + noise, yields a wrong key (caller detects via
   * envelope GCM tag failure). Throws CryptoError('combine_failed') on malformed input.
   */
  combine(shares: RawShare[]): DataKey;
}

// ---------------------------------------------------------------------------
// Shard encryptor (per-recipient ECDH wrap of each raw share)
// ---------------------------------------------------------------------------

/**
 * An encrypted Shamir share — the ONLY shard form on the wire / at the server.
 * Opaque ciphertext: decryptable solely by the holder's private key.
 */
export interface WireShard {
  v: CryptoFormatVersion;
  alg: 'ECDH-P256+HKDF-SHA256+AES-256-GCM';
  recipientKeyId: PublicKeyId; // whose key this is sealed to
  ephemeralPubKey: PublicKeyJwk; // sender's ephemeral ECDH public key
  iv: Base64Url; // 96-bit
  ciphertext: Base64Url; // AES-GCM(share) incl. auth tag
  shareIndex: number; // 1..n, for ceremony progress bookkeeping
}

export interface ShardEncryptor {
  /**
   * Encrypt one raw share to `recipient`. Generates a fresh ephemeral ECDH
   * keypair per call (forward secrecy per shard).
   */
  encryptShare(
    share: RawShare,
    shareIndex: number,
    recipient: { keyId: PublicKeyId; publicKey: PublicKeyJwk },
  ): Promise<WireShard>;

  /**
   * Decrypt the holder's OWN shard with their non-extractable private key handle.
   * Throws CryptoError('shard_tampered') on GCM tag failure,
   * CryptoError('wrong_recipient') if recipientKeyId != the local key id.
   */
  decryptShare(shard: WireShard, privateKey: PrivateKeyHandle): Promise<RawShare>;
}

// ---------------------------------------------------------------------------
// Key manager
// ---------------------------------------------------------------------------

/**
 * Opaque handle to a non-extractable ECDH private key living in IndexedDB.
 * Wraps a WebCrypto CryptoKey (extractable:false). There is NO method to export it.
 */
export interface PrivateKeyHandle {
  readonly keyId: PublicKeyId;
  /** The live WebCrypto key — usable for deriveBits, never exportable. */
  readonly cryptoKey: CryptoKey;
}

export interface KeyPairRecord {
  keyId: PublicKeyId;
  publicKey: PublicKeyJwk;
  createdAt: number; // epoch ms
}

export interface KeyManager {
  /**
   * Generate a per-participant ECDH P-256 keypair. Private key is
   * extractable:false and persisted to IndexedDB; public key is exportable JWK.
   */
  generateKeyPair(): Promise<KeyPairRecord>;

  /** Load the private-key handle for a stored keyId. Throws if absent. */
  getPrivateKey(keyId: PublicKeyId): Promise<PrivateKeyHandle>;

  /** List public-key records held on this device (for registry publish / picker). */
  listLocalKeys(): Promise<KeyPairRecord[]>;

  /** Derive the stable PublicKeyId (= fingerprint hash) from a public key. */
  deriveKeyId(publicKey: PublicKeyJwk): Promise<PublicKeyId>;

  /** Delete a local keypair (lost-device / re-key, M2.10). Irreversible. */
  deleteKeyPair(keyId: PublicKeyId): Promise<void>;
}

// ---------------------------------------------------------------------------
// Fingerprint / safety-number
// ---------------------------------------------------------------------------

/** A stable, comparable out-of-band safety-number for a public key. */
export interface Fingerprint {
  keyId: PublicKeyId;
  /** Lowercase hex of SHA-256(SPKI-DER), grouped for display. */
  hex: string;
  /** Human-readable word list (e.g. 6 words) for verbal comparison. */
  words: string[];
  /** Emoji sequence mirroring `words` for quick visual compare. */
  emoji: string[];
}

export interface FingerprintCodec {
  derive(publicKey: PublicKeyJwk): Promise<Fingerprint>;
  /** Constant-time equality of two fingerprints by keyId. */
  equals(a: Fingerprint, b: Fingerprint): boolean;
}

// ---------------------------------------------------------------------------
// Reveal controller (combine -> open-envelope; plaintext in-browser only)
// ---------------------------------------------------------------------------

export interface RevealResult {
  /** The recovered secret, in-browser only. Must never be logged or transmitted. */
  plaintext: Uint8Array;
  /** Convenience UTF-8 decode for the reveal modal. */
  text: string;
}

export interface RevealController {
  /**
   * At threshold: combine the gathered raw shares into the data key, then open
   * the envelope. Throws CryptoError('quorum_not_met') if fewer than k shares,
   * CryptoError('envelope_tampered') if the recovered key fails the GCM tag.
   */
  reveal(envelope: Envelope, shares: RawShare[], k: number): Promise<RevealResult>;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type CryptoErrorCode =
  | 'envelope_tampered' // GCM tag failed opening the envelope
  | 'shard_tampered' // GCM tag failed decrypting a shard
  | 'wrong_recipient' // shard sealed to a different keyId
  | 'combine_failed' // malformed / insufficient shares
  | 'quorum_not_met' // < k shares supplied to reveal
  | 'unsupported' // capability missing (delegate to capability gate)
  | 'keygen_failed'
  | 'idb_unavailable';

export class CryptoError extends Error {
  constructor(
    readonly code: CryptoErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'CryptoError';
    // Preserve prototype chain across transpilation targets.
    Object.setPrototypeOf(this, CryptoError.prototype);
  }
}
